/* eslint-disable max-len */
import { IPoint, MeshGeometry } from 'pixi.js';

const defaultSettings = {
    thickness: 0,
    textureScale: 0,
    baseline: 0.5,
    repeatDistance: 0,
    flipX: false,
    offsetX: 0,
};

export type SplineGeometrySettings = typeof defaultSettings;

export class SplineGeometry extends MeshGeometry
{
    public settings: SplineGeometrySettings;
    public points: IPoint[];

    constructor(points: IPoint[], settings: Partial<SplineGeometrySettings> = {})
    {
        super(new Float32Array(points.length * 4),
            new Float32Array(points.length * 4),
            new Uint16Array((points.length - 1) * 6));

        this.settings = { ...defaultSettings, ...settings };
        this.points = points;

        this.build();
    }

    private build(): void
    {
        const points = this.points;

        if (!points) return;

        const vertexBuffer = this.getBuffer('aVertexPosition');
        const uvBuffer = this.getBuffer('aTextureCoord');
        const indexBuffer = this.getIndex();

        // if too little points, or texture hasn't got UVs set yet just move on.
        if (points.length < 1)
        {
            return;
        }

        // if the number of points has changed we will need to recreate the arraybuffers
        if (vertexBuffer.data.length / 4 !== points.length)
        {
            vertexBuffer.data = new Float32Array(points.length * 4);
            uvBuffer.data = new Float32Array(points.length * 4);
            indexBuffer.data = new Uint16Array((points.length - 1) * 6);
        }

        const uvs = uvBuffer.data;
        const indices = indexBuffer.data;

        uvs[0] = 0;
        uvs[1] = 0;
        uvs[2] = 0;
        uvs[3] = 1;

        let amount = 0;
        let prevX = points[0].x + this.settings.offsetX;
        let prevY = points[0].y;
        const textureWidth = this.settings.thickness * this.settings.textureScale;
        const total = points.length; // - 1;

        for (let i = 0; i < total; i++)
        {
            // time to do some smart drawing!
            const index = i * 4;

            if (this.settings.textureScale > 0)
            {
                // calculate pixel distance from previous point
                const dx = prevX - points[i].x;
                const dy = prevY - points[i].y;
                const distance = Math.sqrt((dx * dx) + (dy * dy));
                const repeatDistance = this.settings.repeatDistance || textureWidth;

                prevX = points[i].x;
                prevY = points[i].y;
                amount += distance / repeatDistance;
            }
            else
            {
                // stretch texture
                amount = i / (total - 1);
            }

            if (this.settings.flipX)
            {
                uvs[index] = 1 - amount;
                uvs[index + 2] = 1 - amount;
            }
            else
            {
                uvs[index] = amount;
                uvs[index + 2] = amount;
            }
            uvs[index + 1] = 0;
            uvs[index + 3] = 1;
        }

        let indexCount = 0;

        for (let i = 0; i < total - 1; i++)
        {
            const index = i * 2;

            indices[indexCount++] = index;
            indices[indexCount++] = index + 1;
            indices[indexCount++] = index + 2;

            indices[indexCount++] = index + 2;
            indices[indexCount++] = index + 1;
            indices[indexCount++] = index + 3;
        }

        // ensure that the changes are uploaded
        uvBuffer.update();
        indexBuffer.update();

        this.updateVertices();
    }

    public updateVertices(): void
    {
        const points = this.points;

        if (points.length < 1)
        {
            return;
        }

        let lastPoint = points[0];
        let nextPoint;
        let perpX = 0;
        let perpY = 0;

        const vertices = this.buffers[0].data;
        const total = points.length;

        for (let i = 0; i < total; i++)
        {
            const point = points[i];
            const index = i * 4;

            if (i < points.length - 1)
            {
                nextPoint = points[i + 1];
            }
            else
            {
                nextPoint = point;
            }

            perpY = -(nextPoint.x - lastPoint.x);
            perpX = nextPoint.y - lastPoint.y;

            const perpLength = Math.sqrt((perpX * perpX) + (perpY * perpY));
            const num = this.settings.textureScale > 0 ? this.settings.textureScale * this.settings.thickness / 2 : this.settings.thickness / 2;

            perpX /= perpLength;
            perpY /= perpLength;

            perpX *= num;
            perpY *= num;

            const r1 = 2 * this.settings.baseline;
            const r2 = 2 - (2 * this.settings.baseline);

            vertices[index] = point.x + (perpX * r1);
            vertices[index + 1] = point.y + (perpY * r1);
            vertices[index + 2] = point.x - (perpX * r2);
            vertices[index + 3] = point.y - (perpY * r2);

            lastPoint = point;
        }

        this.buffers[0].update();
    }

    public update(): void
    {
        if (this.settings.textureScale > 0)
        {
            this.build(); // we need to update UVs
        }
        else
        {
            this.updateVertices();
        }
    }
}
